import { CodeableConcept, List } from "@medplum/fhirtypes";

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isAfter, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import {
  Gestation,
  PREGNANCY_RECORD_CODING,
  PREGNANCY_RECORD_CODING_QUERY,
  PregnancyRecord,
  selectPregnancyRecordForEncounterDate,
} from "@/pregnancy";
import { middleOfDay } from "@/utils/datetime";
import { joinReferenceString } from "@/utils/medplum";

import { RootState } from "..";

/** state.pregnancy shape: patientId → listId → PregnancyRecord */
type PregnancyState = Record<string, Record<string, PregnancyRecord>>;

const initialState: PregnancyState = {};

const getCurrentPregnancyRecord = (state: PregnancyState, patientId: string) =>
  Object.values(state[patientId] ?? {}).find((record) => record.isActive);

/**
 * Validates that a `PregnancyRecord` has a usable `List.id` and the
 * pregnancy-record List coding. Throws otherwise.
 *
 * @param record - The `PregnancyRecord` to validate
 * @param context - Reducer name used in the thrown error messages
 * @returns The record's `list.id` (guaranteed defined)
 */
const validatePregnancyRecordList = (record: PregnancyRecord, context: string): string => {
  if (!record.list.id) {
    throw new Error(`[${context}] PregnancyRecord requires a valid List ID`);
  }
  if (
    record.list.code?.coding?.every(
      (coding) =>
        coding.system !== PREGNANCY_RECORD_CODING.system ||
        coding.code !== PREGNANCY_RECORD_CODING.code,
    )
  ) {
    throw new Error(`[${context}] List must be of type "${PREGNANCY_RECORD_CODING_QUERY}"`);
  }
  return record.list.id;
};

const pregnancySlice = createSlice({
  name: "pregnancy",
  initialState,
  reducers: {
    /**
     * Add or fully replace a pregnancy record for a patient.
     * Use for both initial loads and historical data backfills.
     *
     * @param patientId - FHIR Patient ID
     * @param record - Full `PregnancyRecord` to store; must have a valid `list.id`
     *   and the pregnancy-record List coding
     */
    upsertPregnancyRecord: (
      state,
      action: PayloadAction<{ patientId: string; record: PregnancyRecord }>,
    ) => {
      const { patientId, record } = action.payload;
      state[patientId] ??= {};
      const listId = validatePregnancyRecordList(record, "upsertPregnancyRecord");
      state[patientId][listId] = record;
    },

    /**
     * Reconcile the stored pregnancy records for a patient against a freshly
     * fetched set, scoped to the fetched `statuses`. Records whose status is
     * within `statuses` but whose `list.id` is absent from the fetched set are
     * evicted (e.g. deleted server-side); records outside the scope are left
     * untouched so partial fetches (e.g. `"current"` only) do not drop retired
     * records. All fetched records are then upserted.
     *
     * @param patientId - FHIR Patient ID
     * @param records - Full set of `PregnancyRecord`s fetched for the scope; each must
     *   have a valid `list.id` and the pregnancy-record List coding
     * @param statuses - Fetched status scope, e.g. `["current", "retired"]`
     */
    syncPatientPregnancyRecords: (
      state,
      action: PayloadAction<{
        patientId: string;
        records: PregnancyRecord[];
        statuses: string[];
      }>,
    ) => {
      const { patientId, records, statuses } = action.payload;
      state[patientId] ??= {};

      const fetched = records.map((record) => ({
        listId: validatePregnancyRecordList(record, "syncPatientPregnancyRecords"),
        record,
      }));
      const fetchedIds = new Set(fetched.map(({ listId }) => listId));

      const scope = new Set(statuses);
      for (const [listId, record] of Object.entries(state[patientId])) {
        const recordStatus = record.isActive ? "current" : "retired";
        if (scope.has(recordStatus) && !fetchedIds.has(listId)) {
          delete state[patientId][listId];
        }
      }

      for (const { listId, record } of fetched) {
        state[patientId][listId] = record;
      }
    },

    /**
     * Remove a single pregnancy record for a patient.
     *
     * @param patientId - FHIR Patient ID
     * @param listId - ID of the pregnancy `List` resource to remove
     */
    removePregnancyRecord: (
      state,
      action: PayloadAction<{ patientId: string; listId: string }>,
    ) => {
      const { patientId, listId } = action.payload;
      delete state[patientId]?.[listId];
    },

    /**
     * Update the EDD for the current active pregnancy record.
     * No-op if there is no active record.
     *
     * @param patientId - FHIR Patient ID
     * @param edd - ISO date string for the estimated due date, or `undefined` to clear
     */
    setCurrentEDD: (
      state,
      action: PayloadAction<{ patientId: string; edd: string | undefined }>,
    ) => {
      const { patientId, edd } = action.payload;
      const record = getCurrentPregnancyRecord(state, patientId);
      if (!record) return;

      record.edd = edd;
    },

    /**
     * Update the EDD for a specific pregnancy record.
     * No-op if the record does not exist.
     *
     * @param patientId - FHIR Patient ID
     * @param listId - ID of the pregnancy `List` resource to update
     * @param edd - ISO date string for the estimated due date, or `undefined` to clear
     */
    setEDD: (
      state,
      action: PayloadAction<{
        patientId: string;
        listId: string;
        edd: string | undefined;
      }>,
    ) => {
      const { patientId, listId, edd } = action.payload;
      const record = state[patientId]?.[listId];
      if (!record) return;

      record.edd = edd;
    },

    /**
     * Update the LMP for the current active pregnancy record.
     * No-op if there is no active record.
     *
     * @param patientId - FHIR Patient ID
     * @param lastMenstrualPeriod - ISO date string for the last menstrual period, or `undefined` to clear
     */
    setCurrentLastMenstrualPeriod: (
      state,
      action: PayloadAction<{ patientId: string; lastMenstrualPeriod: string | undefined }>,
    ) => {
      const { patientId, lastMenstrualPeriod } = action.payload;
      const record = getCurrentPregnancyRecord(state, patientId);
      if (!record) return;

      record.lastMenstrualPeriod = lastMenstrualPeriod;
    },

    /**
     * Update the LMP for a specific pregnancy record.
     * No-op if the record does not exist.
     *
     * @param patientId - FHIR Patient ID
     * @param listId - ID of the pregnancy `List` resource to update
     * @param lastMenstrualPeriod - ISO date string for the last menstrual period, or `undefined` to clear
     */
    setLastMenstrualPeriod: (
      state,
      action: PayloadAction<{
        patientId: string;
        listId: string;
        lastMenstrualPeriod: string | undefined;
      }>,
    ) => {
      const { patientId, listId, lastMenstrualPeriod } = action.payload;
      const record = state[patientId]?.[listId];
      if (!record) return;

      record.lastMenstrualPeriod = lastMenstrualPeriod;
    },

    /**
     * Update the gestational age for the current active pregnancy record.
     * No-op if there is no active record.
     *
     * @param patientId - FHIR Patient ID
     * @param gestationalAge - Computed `Gestation` value, or `undefined` to clear
     */
    setCurrentGestationalAge: (
      state,
      action: PayloadAction<{
        patientId: string;
        gestationalAge: Gestation | undefined;
      }>,
    ) => {
      const { patientId, gestationalAge } = action.payload;
      const record = getCurrentPregnancyRecord(state, patientId);
      if (!record) return;

      record.gestationalAge = gestationalAge;
    },

    /**
     * Update the gestational age for a specific pregnancy record.
     * No-op if the record does not exist.
     *
     * @param patientId - FHIR Patient ID
     * @param listId - ID of the pregnancy `List` resource to update
     * @param gestationalAge - Computed `Gestation` value, or `undefined` to clear
     */
    setGestationalAge: (
      state,
      action: PayloadAction<{
        patientId: string;
        listId: string;
        gestationalAge: Gestation | undefined;
      }>,
    ) => {
      const { patientId, listId, gestationalAge } = action.payload;
      const record = state[patientId]?.[listId];
      if (!record) return;

      record.gestationalAge = gestationalAge;
    },

    /**
     * Update the delivery date for a specific pregnancy record.
     * No-op if the record does not exist.
     *
     * @param patientId - FHIR Patient ID
     * @param listId - ID of the pregnancy `List` resource to update
     * @param deliveryDate - ISO date string for the delivery/close-out date, or `undefined` to clear
     */
    setDeliveryDate: (
      state,
      action: PayloadAction<{
        patientId: string;
        listId: string;
        deliveryDate: string | undefined;
      }>,
    ) => {
      const { patientId, listId, deliveryDate } = action.payload;
      const record = state[patientId]?.[listId];
      if (!record) return;

      record.deliveryDate = deliveryDate;
    },

    /**
     * Update the postpartum age for a specific pregnancy record.
     * No-op if the record does not exist.
     *
     * @param patientId - FHIR Patient ID
     * @param listId - ID of the pregnancy `List` resource to update
     * @param postpartumAge - Computed `Gestation` value, or `undefined` to clear
     */
    setPostpartumAge: (
      state,
      action: PayloadAction<{
        patientId: string;
        listId: string;
        postpartumAge: Gestation | undefined;
      }>,
    ) => {
      const { patientId, listId, postpartumAge } = action.payload;
      const record = state[patientId]?.[listId];
      if (!record) return;

      record.postpartumAge = postpartumAge;
    },

    /**
     * Replace the raw `List` resource on an existing pregnancy record.
     * Throws if `list.id` is missing or the record is not already in state.
     *
     * @param patientId - FHIR Patient ID
     * @param list - Updated FHIR `List` resource; must have a valid `id`
     */
    setList: (state, action: PayloadAction<{ patientId: string; list: List }>) => {
      const { patientId, list } = action.payload;
      if (!list.id) {
        throw new Error("[setList] PregnancyRecord requires a valid List ID");
      }
      const record = state[patientId]?.[list.id];
      record.list = list;
    },

    /**
     * Optimistically upsert an Encounter entry on a pregnancy record's List in
     * Redux state — mirrors the server-side `upsertEncounterToPregnancyRecord`
     * service. Prefers the active pregnancy when present; otherwise links to a
     * retired prenatal/postpartum record that contains `date` (e.g. historical
     * create after close-out). No-op when neither applies. Use after creating a
     * new encounter so that `selectPregnancyRecordForEncounter` resolves
     * immediately without a server round-trip.
     *
     * @param patientId - FHIR Patient ID
     * @param encounterId - ID of the newly created Encounter
     * @param flag - Optional `Encounter.serviceType` to store on the entry
     * @param date - Optional `Encounter.period.start` to store on the entry and
     *   to match a retired pregnancy when no active record exists
     */
    upsertEncounterToPregnancyRecord: (
      state,
      action: PayloadAction<{
        patientId: string;
        encounterId: string;
        flag?: CodeableConcept;
        date?: string;
      }>,
    ) => {
      const { patientId, encounterId, flag, date } = action.payload;
      const currentRecord = getCurrentPregnancyRecord(state, patientId);
      const record =
        currentRecord ??
        selectPregnancyRecordForEncounterDate(Object.values(state[patientId] ?? {}), date);
      if (!record) return;

      const reference = joinReferenceString("Encounter", encounterId);
      const existing = record.list.entry?.find((entry) => entry.item?.reference === reference);
      if (existing) {
        if (flag !== undefined) existing.flag = flag;
        if (date !== undefined) existing.date = date;
        return;
      }

      record.list.entry = [...(record.list.entry ?? []), { item: { reference }, flag, date }];
    },
  },
});

export const {
  upsertPregnancyRecord,
  syncPatientPregnancyRecords,
  removePregnancyRecord,
  setCurrentEDD,
  setEDD,
  setCurrentLastMenstrualPeriod,
  setLastMenstrualPeriod,
  setCurrentGestationalAge,
  setGestationalAge,
  setDeliveryDate,
  setPostpartumAge,
  setList,
  upsertEncounterToPregnancyRecord,
} = pregnancySlice.actions;

// --- Base input selector ------------------------------------------------------

/**
 * Stable empty object returned when a patient has no pregnancy records.
 * Using a module-level constant avoids creating a new reference on every selector
 * call, which would otherwise break reselect's input-stability check.
 */
const EMPTY_PATIENT_RECORDS: Record<string, PregnancyRecord> = {};

const selectPatientPregnancyRecords = (state: RootState, patientId: string) =>
  state.pregnancy[patientId] ?? EMPTY_PATIENT_RECORDS;

// --- Record selectors ---------------------------------------------------------

/** A specific pregnancy record by patientId + listId. */
export const selectPregnancyRecord = (patientId: string, listId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (records) => records[listId],
  );

/** All pregnancy records for a patient (current + historical), latest first. */
export const selectAllPatientPregnancyRecords = (patientId: string) =>
  createSelector([(state: RootState) => selectPatientPregnancyRecords(state, patientId)], (lists) =>
    Object.values(lists).sort((a, b) => (a.list.date === b.list.date ? 0 : a.list.date ? -1 : 1)),
  );

/** The active/current pregnancy record for a patient, if any. */
export const selectCurrentPregnancyRecord = (patientId: string) =>
  createSelector([(state: RootState) => selectPatientPregnancyRecords(state, patientId)], (lists) =>
    Object.values(lists).find((r) => r.isActive),
  );

/** The `List.meta.versionId` of the active pregnancy record, used to detect server-side updates. */
export const selectCurrentPregnancyVersionId = (patientId: string) =>
  createSelector(
    [selectCurrentPregnancyRecord(patientId)],
    (record) => record?.list.meta?.versionId,
  );

/** All past (inactive) pregnancy records for a patient. */
export const selectPastPregnancyRecords = (patientId: string) =>
  createSelector([(state: RootState) => selectPatientPregnancyRecords(state, patientId)], (lists) =>
    Object.values(lists).filter((r) => !r.isActive),
  );

/**
 * The most recently ended past pregnancy record for a patient, sorted by `endDateTime` descending.
 *
 * @param patientId - FHIR `Patient` ID
 * @returns The `PregnancyRecord` with the latest `endDateTime`, or `undefined` if none exist
 */
export const selectMostRecentPastPregnancyRecord = (patientId: string) =>
  createSelector([selectPastPregnancyRecords(patientId)], (pastRecords) =>
    [...pastRecords]
      .sort((recordA, recordB) =>
        (recordB.endDateTime ?? "").localeCompare(recordA.endDateTime ?? ""),
      )
      .at(0),
  );

/**
 * The most recently ended past pregnancy record whose delivery anchor (calendar day) is on or
 * before `encounterStartPeriod`'s calendar day, ensuring historical encounters are matched
 * against the delivery that actually preceded them rather than a later one.
 *
 * The delivery anchor is `deliveryDate` when present (the actual birth date, which can be
 * updated independently by GTPALDrawer), falling back to `endDateTime` (the administrative
 * pregnancy close-out date). Calendar-day comparison (first 10 chars of ISO string) is used
 * so that a noon-UTC timestamp does not shift the anchor day in negative-offset timezones.
 *
 * @param patientId - FHIR `Patient` ID
 * @param encounterStartPeriod - ISO datetime string of the encounter's `period.start`
 * @returns The matching `PregnancyRecord`, or `undefined` if no past record predates the encounter
 */
export const selectMostRecentPastPregnancyRecordForEncounter = (
  patientId: string,
  encounterStartPeriod: string,
) =>
  createSelector([selectPastPregnancyRecords(patientId)], (pastRecords) => {
    if (!encounterStartPeriod) return undefined;

    const encounterDate = middleOfDay(
      formatInTimeZone(parseISO(encounterStartPeriod), "UTC", "yyyy-MM-dd"),
    )!;

    return [...pastRecords]
      .filter((record) => {
        const anchor = record.deliveryDate ?? record.endDateTime;
        if (!anchor) return false;
        const anchorDate = middleOfDay(formatInTimeZone(parseISO(anchor), "UTC", "yyyy-MM-dd"))!;
        return !isAfter(anchorDate, encounterDate);
      })
      .sort((recordA, recordB) =>
        (recordB.endDateTime ?? "").localeCompare(recordA.endDateTime ?? ""),
      )
      .at(0);
  });

// --- Current-pregnancy field selectors ---------------------------------------

/** Whether the patient has an active pregnancy. */
export const selectIsPregnant = (patientId: string) =>
  createSelector([(state: RootState) => selectPatientPregnancyRecords(state, patientId)], (lists) =>
    Object.values(lists).some((r) => r.isActive),
  );

/** EDD of the current pregnancy. Undefined if not currently pregnant. */
export const selectCurrentEDD = (patientId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => Object.values(lists).find((r) => r.isActive)?.edd,
  );

/** LMP of the current pregnancy. Undefined if not currently pregnant. */
export const selectCurrentLastMenstrualPeriod = (patientId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => Object.values(lists).find((r) => r.isActive)?.lastMenstrualPeriod,
  );

/** Gestational age of the current pregnancy. Undefined if not currently pregnant. */
export const selectCurrentGestationalAge = (patientId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => Object.values(lists).find((r) => r.isActive)?.gestationalAge,
  );

/** Pregnancy record whose List contains the given encounter as an entry item. */
export const selectPregnancyRecordForEncounter = (patientId: string, encounterId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (records) =>
      Object.values(records).find((record) =>
        record.list.entry?.some(
          (entry) => entry.item?.reference === joinReferenceString("Encounter", encounterId),
        ),
      ),
  );

// --- Per-record field selectors -----------------------------------------------

/** EDD for a specific pregnancy record. */
export const selectEDD = (patientId: string, listId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => lists[listId]?.edd,
  );

/** LMP for a specific pregnancy record. */
export const selectLastMenstrualPeriod = (patientId: string, listId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => lists[listId]?.lastMenstrualPeriod,
  );

/** Gestational age for a specific pregnancy record. */
export const selectGestationalAge = (patientId: string, listId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => lists[listId]?.gestationalAge,
  );

/** Delivery date for a specific pregnancy record. */
export const selectDeliveryDate = (patientId: string, listId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => lists[listId]?.deliveryDate,
  );

/** Postpartum age snapshot for a specific pregnancy record. */
export const selectPostpartumAge = (patientId: string, listId: string) =>
  createSelector(
    [(state: RootState) => selectPatientPregnancyRecords(state, patientId)],
    (lists) => lists[listId]?.postpartumAge,
  );

/** Delivery date of the most recently ended past pregnancy. */
export const selectMostRecentPastDeliveryDate = (patientId: string) =>
  createSelector(
    [selectMostRecentPastPregnancyRecord(patientId)],
    (record) => record?.deliveryDate,
  );

/** Postpartum age snapshot of the most recently ended past pregnancy. */
export const selectMostRecentPostpartumAge = (patientId: string) =>
  createSelector(
    [selectMostRecentPastPregnancyRecord(patientId)],
    (record) => record?.postpartumAge,
  );

export default pregnancySlice.reducer;
